import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Send, Zap, RefreshCw, MessageSquare, X, GripVertical } from 'lucide-react';
import { useSmartReplies } from '@/modules/ai-management';
import { useQuickReplies } from '@/hooks/useQuickReplies';
import { MediaInput } from './MediaInput';
import { MediaPreview } from './MediaPreview';
import { EmojiPicker } from './EmojiPicker';
import { AISuggestionBar } from './AISuggestionBar';
import { AISuggestionLoadingBar } from './AISuggestionLoadingBar';
import { PaymentButton } from './PaymentButton';
import { mediaUploadService, MediaFile } from '@/services/mediaUploadService';
import { useToast } from '@/hooks/use-toast';
import { useAISuggestionsContext } from '@/contexts/AISuggestionsContext';
import type { Message } from '@/types/message-types';
import type { AIMode } from './AIAgentModeSelector';
import { useLogging } from '@/shared/hooks/useLogging';


interface MessageInputProps {
  conversationId: string;
  messages: Message[];
  newMessage: string;
  onNewMessageChange: (value: string) => void;
  onSendMessage: (content: string, messageType: string, modelId?: string, mediaFiles?: MediaFile[], aiSuggestionData?: Record<string, unknown>) => void;
  onRefreshMessages: () => void;
  sending: boolean;
  isManualRefreshing: boolean;
  tenantId: string;
  customerId: string;
  customerPhone?: string;
  aiMode: AIMode;
  isWindowClosed?: boolean;
  // N-15: lets the closed-window state open the same template selector the error panel
  // uses, instead of leaving the operator with a disabled box and no door through it.
  onOpenTemplateSelector?: () => void;
}

export function MessageInput({
  conversationId,
  messages,
  newMessage,
  onNewMessageChange,
  onSendMessage,
  onRefreshMessages,
  sending,
  isManualRefreshing,
  tenantId,
  customerId,
  customerPhone,
  aiMode,
  isWindowClosed,
  onOpenTemplateSelector
}: MessageInputProps) {
  const [showSmartReplies, setShowSmartReplies] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [isAISuggestion, setIsAISuggestion] = useState(false);
  const [originalAIContent, setOriginalAIContent] = useState<string>('');
  const [isResizing, setIsResizing] = useState(false);
  const [textareaHeight, setTextareaHeight] = useState(60);
  const [hasManuallyResized, setHasManuallyResized] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { t } = useTranslation('conversations');
  const { toast } = useToast();
  const { debug } = useLogging('whatsapp');

  const { generateReplies, smartReplies, loading: generatingReplies } = useSmartReplies(conversationId);
  const { quickReplies, loading: loadingQuickReplies, incrementUsageCount } = useQuickReplies(tenantId);
  const { 
    currentSuggestion, 
    loading: loadingSuggestion, 
    clearSuggestion
  } = useAISuggestionsContext();

  // Utility functions for AI suggestion metadata (moved from hook since we only consume from context now)
  const calculateChangeMetadata = useCallback((originalContent: string, finalContent: string) => {
    const totalChanges = Math.abs(finalContent.length - originalContent.length);
    const changePercentage = originalContent.length > 0 ? (totalChanges / originalContent.length) * 100 : 0;
    
    const changes = {
      charactersAdded: Math.max(0, finalContent.length - originalContent.length),
      charactersRemoved: Math.max(0, originalContent.length - finalContent.length),
      wordsAdded: finalContent.split(' ').length - originalContent.split(' ').length,
      hasSignificantChange: Math.abs(finalContent.length - originalContent.length) > 10,
      hasContentChanges: originalContent !== finalContent,
      changePercentage: Math.round(changePercentage)
    };
    return changes;
  }, []);

  const determineSenderType = useCallback((originalContent: string, finalContent: string) => {
    if (finalContent.length > originalContent.length * 1.5) {
      return 'ai_with_human_review_with_changes';
    } else if (finalContent !== originalContent) {
      return 'ai_with_human_review_with_changes';
    }
    return 'ai_with_human_review_no_changes';
  }, []);

  // Enhanced debug logging for AI suggestion state with render details
  // State for tracking suggestion display with recovery
  const suggestionDisplayRef = useRef(null);
  const lastSuggestionIdRef = useRef(null);
  
  // Add fallback recovery for lost suggestion state
  useEffect(() => {
    if (currentSuggestion && currentSuggestion.id !== lastSuggestionIdRef.current) {
      debug('AI suggestion display: New suggestion detected', {
        suggestionId: currentSuggestion.id,
        previousId: lastSuggestionIdRef.current,
        content: currentSuggestion.content.substring(0, 50),
        aiMode
      });
      lastSuggestionIdRef.current = currentSuggestion.id;
      suggestionDisplayRef.current = currentSuggestion;
    }
    
    // Fallback recovery: if we lost currentSuggestion but had one recently, attempt recovery
    if (!currentSuggestion && suggestionDisplayRef.current && aiMode === 'suggestions') {
      debug('AI suggestion recovery: Attempting to recover lost suggestion', {
        lostSuggestionId: suggestionDisplayRef.current.id,
        aiMode
      });
      
      // Clear the display ref after recovery attempt
      setTimeout(() => {
        suggestionDisplayRef.current = null;
        lastSuggestionIdRef.current = null;
      }, 5000);
    }
  }, [currentSuggestion, aiMode, debug]);

  const shouldShowSuggestion = !!(currentSuggestion && aiMode === 'suggestions');

  // Track suggestion state changes
  React.useEffect(() => {
    if (currentSuggestion) {
      debug('AI suggestion state change: New suggestion in MessageInput', {
        suggestionId: currentSuggestion.id,
        content: currentSuggestion.content,
        confidence: currentSuggestion.confidence,
        aiMode,
        willShowSuggestionBar: aiMode === 'suggestions'
      });
    } else {
      debug('AI suggestion state change: Suggestion cleared', { aiMode });
    }
  }, [currentSuggestion, aiMode, debug]);

  const handleSubmit = useCallback(async (e: React.FormEvent | null, contentOverride?: string) => {
    if (e) e.preventDefault();
    const messageContent = contentOverride || newMessage;
    if ((!messageContent.trim() && mediaFiles.length === 0) || sending || uploadingMedia || isWindowClosed) return;
    
    let finalMediaFiles = mediaFiles;
    
    // Upload media files if any
    if (mediaFiles.length > 0) {
      setUploadingMedia(true);
      const uploadedFiles: MediaFile[] = [];
      
      for (const mediaFile of mediaFiles) {
        if (mediaFile.status === 'ready') {
          // Update status to uploading
          const updatedMediaFile = { ...mediaFile, status: 'uploading' as const };
          
          const uploadResult = await mediaUploadService.uploadMedia(mediaFile.file, tenantId);
          if (uploadResult.success) {
            console.log(`📤 Media upload successful for ${mediaFile.file.name}:`, {
              fileUrl: uploadResult.fileUrl,
              fileName: uploadResult.fileName,
              mimeType: uploadResult.mimeType
            });
            uploadedFiles.push({
              ...updatedMediaFile,
              status: 'ready',
              mediaUrl: uploadResult.fileUrl
            });
          } else {
            console.error(`❌ Media upload failed for ${mediaFile.file.name}:`, uploadResult.error);
            toast({
              variant: "destructive",
              title: t('media.uploadFailed'),
              description: t('media.uploadFailedDesc', { filename: mediaFile.file.name, error: uploadResult.error })
            });
            uploadedFiles.push({ ...mediaFile, status: 'error' });
          }
        } else {
          uploadedFiles.push(mediaFile);
        }
      }
      
      finalMediaFiles = uploadedFiles;
      setUploadingMedia(false);
    }
    
    // Determine message type
    const messageType = finalMediaFiles.length > 0 ? finalMediaFiles[0].type : 'text';
    const content = messageContent.trim() || (finalMediaFiles.length > 0 ? `[${messageType.toUpperCase()}]` : '');
    
    // Prepare AI suggestion data if this is an AI suggestion being sent
    let aiSuggestionData = null;
    if (isAISuggestion && originalAIContent) {
      const changeMetadata = calculateChangeMetadata(originalAIContent, content);
      const senderType = determineSenderType(originalAIContent, content);
      
      aiSuggestionData = {
        originalAIContent,
        isAIResponse: true,
        hasHumanModifications: changeMetadata.hasContentChanges,
        modificationType: changeMetadata.changePercentage > 50 ? 'major' : 
                         changeMetadata.changePercentage > 10 ? 'minor' : 'none',
        aiSource: currentSuggestion?.aiSource,
        humanReview: {
          reviewedBy: tenantId, // Use actual user ID if available
          reviewedAt: new Date().toISOString(),
          changesDescription: changeMetadata.hasContentChanges ? 'Content modified by human' : 'Sent without changes'
        },
        senderType,
        changeMetadata
      };
    }

    onSendMessage(content, messageType, undefined, finalMediaFiles, aiSuggestionData);
    
    // Only clear input if not using content override (i.e., when sending from manual input)
    if (!contentOverride) {
      onNewMessageChange('');
    }
    setMediaFiles([]);
    setShowSmartReplies(false);
    setIsAISuggestion(false);
    setOriginalAIContent('');
  }, [newMessage, mediaFiles, sending, uploadingMedia, onSendMessage, onNewMessageChange, tenantId, toast, isAISuggestion, originalAIContent, calculateChangeMetadata, determineSenderType, currentSuggestion]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);

    const startY = e.clientY;
    const startHeight = textareaRef.current?.offsetHeight || 60;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      // Calculate new height: dragging UP (negative delta) INCREASES height
      const deltaY = startY - moveEvent.clientY;
      const newHeight = Math.min(
        Math.max(startHeight + deltaY, 60),  // min: 60px
        400  // max: 400px
      );
      setTextareaHeight(newHeight);

      if (textareaRef.current) {
        textareaRef.current.style.height = `${newHeight}px`;
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setHasManuallyResized(true);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const handleSmartReplyClick = useCallback(async () => {
    if (messages.length === 0) return;
    
    setShowSmartReplies(true);
    
    // Get last few messages for context - use last customer message
    const customerMessages = messages.filter(m => m.sender_type === 'customer');
    const lastCustomerMessage = customerMessages[customerMessages.length - 1]?.content || '';
    const replies = await generateReplies(messages.slice(-5), lastCustomerMessage);
    
    // Only show error if no replies were generated and it's not a configuration issue
    if (replies.length === 0) { // no-op: intentional empty block
      
    }
  }, [messages, conversationId, generateReplies]);

  const handleSmartReplySelect = useCallback((reply: string) => {
    onNewMessageChange(reply);
    setShowSmartReplies(false);
    textareaRef.current?.focus();
  }, [onNewMessageChange]);

  const handleMediaSelect = useCallback((files: MediaFile[]) => {
    setMediaFiles(prevFiles => [...prevFiles, ...files]);
  }, []);

  const handleRemoveMediaFile = useCallback((fileId: string) => {
    setMediaFiles(prevFiles => prevFiles.filter(file => file.id !== fileId));
  }, []);

  const handleEmojiSelect = useCallback((emoji: string) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = newMessage;
      const newValue = currentValue.slice(0, start) + emoji + currentValue.slice(end);
      onNewMessageChange(newValue);
      
      // Set cursor position after emoji
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      }, 0);
    } else {
      onNewMessageChange(newMessage + emoji);
    }
  }, [newMessage, onNewMessageChange]);

  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const clipboardItems = Array.from(e.clipboardData.files);
    const imageFiles = clipboardItems.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      e.preventDefault();
      
      const validFiles: MediaFile[] = [];
      const invalidFiles: string[] = [];
      
      for (const file of imageFiles) {
        if (mediaUploadService.validateFileType(file) && mediaUploadService.validateFileSize(file)) {
          const mediaFile = mediaUploadService.createMediaFile(file);
          validFiles.push(mediaFile);
        } else {
          invalidFiles.push(file.name);
        }
      }
      
      if (validFiles.length > 0) {
        setMediaFiles(prevFiles => [...prevFiles, ...validFiles]);
        toast({
          title: t('media.imagesPasted'),
          description: t('media.imagesPastedDesc', { count: validFiles.length }),
        });
      }

      if (invalidFiles.length > 0) {
        toast({
          variant: "destructive",
          title: t('media.someFilesNotAdded'),
          description: t('media.someFilesNotAddedDesc', { filenames: invalidFiles.join(', ') }),
        });
      }
    }
  }, [setMediaFiles, toast, t]);

  // AI suggestions are now only generated for NEW webhook messages via useRealtimeMessages
  // This prevents excessive API calls when browsing old conversations

  // Handle AI suggestion acceptance - Send immediately
  const handleAcceptSuggestion = useCallback(async () => {
    if (currentSuggestion) {
      if (isWindowClosed) return;
      // Prepare AI suggestion data
      const aiSuggestionData = {
        originalAIContent: currentSuggestion.content,
        isAIResponse: true,
        hasHumanModifications: false,
        modificationType: 'none',
        aiSource: currentSuggestion.aiSource,
        humanReview: {
          reviewedBy: tenantId,
          reviewedAt: new Date().toISOString(),
          changesDescription: 'Sent without changes'
        },
        senderType: 'ai_with_human_review_no_changes' as const,
        changeMetadata: calculateChangeMetadata(currentSuggestion.content, currentSuggestion.content)
      };

      // Send message immediately with AI suggestion data
      onSendMessage(currentSuggestion.content, 'text', undefined, [], aiSuggestionData);
      
      // Clear states
      clearSuggestion();
      setIsAISuggestion(false);
      setOriginalAIContent('');
    }
  }, [currentSuggestion, onSendMessage, tenantId, clearSuggestion, calculateChangeMetadata, isWindowClosed]);

  // Handle AI suggestion editing
  const handleEditSuggestion = useCallback(async (editedContent: string) => {
    if (currentSuggestion) {
      if (isWindowClosed) return;
      // Prepare AI suggestion data for edited message
      const changeMetadata = calculateChangeMetadata(currentSuggestion.content, editedContent);
      const senderType = determineSenderType(currentSuggestion.content, editedContent);
      
      const aiSuggestionData = {
        originalAIContent: currentSuggestion.content,
        isAIResponse: true,
        hasHumanModifications: changeMetadata.hasContentChanges,
        modificationType: changeMetadata.changePercentage > 50 ? 'major' : 
                         changeMetadata.changePercentage > 10 ? 'minor' : 'none',
        aiSource: currentSuggestion.aiSource,
        humanReview: {
          reviewedBy: tenantId,
          reviewedAt: new Date().toISOString(),
          changesDescription: changeMetadata.hasContentChanges ? 'Content modified by human' : 'Sent without changes'
        },
        senderType,
        changeMetadata
      };

      // Send message directly with AI suggestion data
      onSendMessage(editedContent, 'text', undefined, [], aiSuggestionData);
      
      // Clear states
      clearSuggestion();
    }
  }, [currentSuggestion, clearSuggestion, onSendMessage, tenantId, calculateChangeMetadata, determineSenderType, isWindowClosed]);

  // Handle AI suggestion discard
  const handleDiscardSuggestion = useCallback(() => {
    clearSuggestion();
    setIsAISuggestion(false);
    setOriginalAIContent('');
  }, [clearSuggestion]);

  return (
    <Card>
      <CardContent className="p-3 space-y-2">
        {/* N-15: a closed 24h window used to just disable the box with no way through it.
            This offers the real path -- resuming with an approved template -- instead of
            silence. */}
        {isWindowClosed && onOpenTemplateSelector && (
          <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/50 px-3 py-2">
            <span className="text-sm text-muted-foreground">{t('chat.windowClosed')}</span>
            <Button size="sm" onClick={onOpenTemplateSelector}>
              {t('chat.resumeWithTemplate')}
            </Button>
          </div>
        )}

        {/* Media Preview */}
        {mediaFiles.length > 0 && (
          <MediaPreview
            mediaFiles={mediaFiles}
            onRemoveFile={handleRemoveMediaFile}
          />
        )}

        {/* AI Assist Mode Interface */}
        {aiMode === 'suggestions' && (
          currentSuggestion ? (
            <AISuggestionBar
              suggestion={currentSuggestion}
              onAccept={handleAcceptSuggestion}
              onEdit={handleEditSuggestion}
              onDiscard={handleDiscardSuggestion}
              disabled={sending || uploadingMedia || isWindowClosed}
              aiMode={aiMode}
            />
          ) : (
            <AISuggestionLoadingBar
              onDiscard={handleDiscardSuggestion}
              disabled={sending || uploadingMedia || isWindowClosed}
              state={loadingSuggestion ? 'generating' : 'awaiting'}
              aiMode={aiMode}
            />
          )
        )}

        {/* Quick Replies (Canned Responses) */}
        {showQuickReplies && quickReplies.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                <span className="text-sm font-medium">{t('quickReplies.title')}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowQuickReplies(false)}
                className="h-6 w-6 p-0 hover:bg-muted"
                title={t('quickReplies.close')}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {quickReplies.map((reply) => (
                <Button
                  key={reply.id}
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    onNewMessageChange(reply.content);
                    incrementUsageCount(reply.id);
                  }}
                  className="text-left"
                >
                  {reply.template_name}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Smart Replies */}
        {showSmartReplies && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4" />
                <span className="text-sm font-medium">{t('smartReplies.title')}</span>
                {generatingReplies && (
                  <div className="animate-spin rounded-full h-3 w-3 border-b border-primary" />
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSmartReplies(false)}
                className="h-6 w-6 p-0 hover:bg-muted"
                title={t('messageInput.closeSmartReplies')}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {smartReplies.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {smartReplies.map((reply, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSmartReplySelect(reply.content)}
                    className="text-left"
                  >
                    {reply.content}
                  </Button>
                ))}
              </div>
            ) : generatingReplies ? (
              <p className="text-sm text-muted-foreground">{t('smartReplies.generating')}</p>
            ) : (
              <p className="text-sm text-muted-foreground">{t('smartReplies.noRepliesAvailable')}</p>
            )}
          </div>
        )}

        {/* Manual Input Form - Only show when NOT in AI Assist mode */}
        {aiMode !== 'suggestions' && (
          <form onSubmit={handleSubmit} className="space-y-3">
              {/* Main Input Row */}
              <div className="flex items-end space-x-2">
                <div className="flex-1 relative">
                  <Textarea
                    ref={textareaRef}
                    value={newMessage}
                    onChange={(e) => onNewMessageChange(e.target.value)}
                    onPaste={handlePaste}
                    placeholder={isWindowClosed ? t('chat.windowClosed') : (isAISuggestion ? t('aiSuggestion.inputPlaceholder') : t('typeMessage'))}
                    className={`resize-none flex-1 ${isAISuggestion ? 'border-primary/50 bg-primary/5' : ''}`}
                    disabled={sending || uploadingMedia || isWindowClosed}
                    autoGrow={!hasManuallyResized}
                    minHeight={60}
                    maxHeight={400}
                    style={{ height: `${textareaHeight}px` }}
                  />
                  {/* Drag Handle - Top Right */}
                  <div
                    className="absolute top-1 right-1 text-muted-foreground/50 hover:text-muted-foreground cursor-ns-resize"
                    onMouseDown={handleResizeStart}
                  >
                    <GripVertical className="h-4 w-4" />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={(!newMessage.trim() && mediaFiles.length === 0) || sending || uploadingMedia || isWindowClosed}
                  className="shrink-0 h-[60px] px-4"
                >
                  <Send className="h-5 w-5" />
                </Button>
              </div>

              {/* Bottom Toolbar */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1">
                  <MediaInput
                    onMediaSelect={handleMediaSelect}
                    disabled={sending || uploadingMedia}
                  />

                  <EmojiPicker
                    onEmojiSelect={handleEmojiSelect}
                  />

                  <PaymentButton
                    conversationId={conversationId}
                    onPaymentCreated={onRefreshMessages}
                    disabled={sending || uploadingMedia}
                  />

                  {messages.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleSmartReplyClick}
                      disabled={generatingReplies || messages.length === 0}
                      className="text-xs"
                    >
                      <Zap className="h-4 w-4" />
                    </Button>
                  )}

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onRefreshMessages}
                    disabled={isManualRefreshing}
                  >
                    <RefreshCw className={`h-4 w-4 ${isManualRefreshing ? 'animate-spin' : ''}`} />
                  </Button>
                </div>

                <div className="text-xs text-muted-foreground">
                  {sending && "Sending..."}
                  {uploadingMedia && "Uploading media..."}
                  {!sending && !uploadingMedia && mediaFiles.length > 0 && `${mediaFiles.length} file(s)`}
                </div>
              </div>

              {/* Status Indicators */}
              {(sending || uploadingMedia) && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="animate-spin rounded-full h-3 w-3 border-b border-primary" />
                  <span>{uploadingMedia ? 'Uploading media...' : 'Sending message...'}</span>
                </div>
              )}

              
              {isAISuggestion && (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs border-primary text-primary">
                    AI Suggestion - Edit as needed
                  </Badge>
                </div>
              )}
          </form>
        )}
      </CardContent>
    </Card>
  );
}